const base = () => '/api';

async function request<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${base()}${url}`, {
    credentials: 'same-origin',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...opts?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export function checkAuth() {
  return request<{ user: { id: number; username: string } }>('/me');
}

export function login(username: string, password: string) {
  return request<{ user: { id: number; username: string } }>('/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export function register(username: string, password: string) {
  return request<{ user: { id: number; username: string } }>('/register', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export function logout() {
  return request<void>('/logout', { method: 'POST' });
}

export function fetchSources() {
  return request<{
    sources: import('./types').Source[];
    hiddenIds: number[];
    interestText: string;
    classifierRoles: Record<number, 'interest' | 'no-interest' | 'hide'>;
    classifiers: { id: number; name: string }[];
  }>('/sources');
}

export function fetchHeadlines(opts?: {
  sourceSlug?: string;
  classifierId?: number;
}) {
  const params = new URLSearchParams();
  if (opts?.sourceSlug) params.set('source', opts.sourceSlug);
  if (opts?.classifierId) params.set('classifier', String(opts.classifierId));
  const qs = params.toString() ? `?${params}` : '';
  return request<{
    unread: import('./types').Headline[];
    read: import('./types').ReadHeadline[];
    interestMode: 'text' | 'classifiers' | null;
  }>(`/headlines${qs}`);
}

export function clickHeadline(id: number) {
  return request<{ url: string }>(`/click/${id}`, { method: 'POST' });
}

export function fetchSummary(id: number) {
  return request<{ sentences: string[] }>(`/summary/${id}`);
}

export function markAllRead(ids: number[]) {
  return request<void>('/mark-all-read', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export function saveSettings(
  enabledSourceIds: number[],
  interestText: string,
  classifierRoles?: Record<number, 'interest' | 'no-interest' | 'hide' | null>,
) {
  return request<void>('/settings', {
    method: 'PUT',
    body: JSON.stringify({ enabledSourceIds, interestText, classifierRoles }),
  });
}

export function fetchClassifierState(headlineId: number) {
  return request<{
    classifiers: {
      id: number;
      name: string;
      role: string | null;
      probability: number | null;
      accepted: boolean;
      examplePolarity: 'positive' | 'negative' | null;
    }[];
    hasEmbedding: boolean;
  }>(`/headlines/${headlineId}/classifier-state`);
}

export function saveClassifierCorrections(
  headlineId: number,
  corrections: {
    classifierId: number;
    polarity: 'positive' | 'negative' | null;
  }[],
) {
  return request<{
    results: { classifierId: number; stats: unknown }[];
  }>(`/headlines/${headlineId}/classifier-corrections`, {
    method: 'POST',
    body: JSON.stringify({ corrections }),
  });
}

export function createFilter(
  name: string,
  prototypes: { headlineId: number; polarity: string }[],
) {
  return request<{ id: number }>('/filters', {
    method: 'POST',
    body: JSON.stringify({ name, prototypes }),
  });
}

export function fetchFilters() {
  return request<{
    filters: { id: number; name: string; created_at: string }[];
  }>('/filters');
}

export function applyFilter(filterId: number) {
  return request<{
    results: {
      headline_id: number;
      title: string;
      url: string;
      source: string;
      score: number;
    }[];
  }>(`/filters/${filterId}/apply`);
}

export function deleteFilter(filterId: number) {
  return request<void>(`/filters/${filterId}`, { method: 'DELETE' });
}

export function createClassifier(
  name: string,
  examples: { headlineId: number; polarity: string }[],
) {
  return request<{
    id: number;
    stats: {
      trainSize: number;
      positives: number;
      negatives: number;
      epochs: number;
      finalLoss: number | null;
    };
  }>('/classifiers', {
    method: 'POST',
    body: JSON.stringify({ name, examples }),
  });
}

export function fetchClassifiers() {
  return request<{
    classifiers: {
      id: number;
      name: string;
      created_at: string;
      stats: string;
      threshold: string;
    }[];
  }>('/classifiers');
}

export function applyClassifier(classifierId: number) {
  return request<{
    results: {
      headline_id: number;
      title: string;
      url: string;
      source: string;
      probability: number;
    }[];
    examples: {
      headline_id: number;
      title: string;
      url: string;
      source: string;
      polarity: string;
      probability: number;
    }[];
    threshold: number;
  }>(`/classifiers/${classifierId}/apply`);
}

export function addClassifierExample(
  classifierId: number,
  headlineId: number,
  polarity: 'positive' | 'negative',
) {
  return request<{ stats: Record<string, unknown> }>(
    `/classifiers/${classifierId}/examples`,
    {
      method: 'POST',
      body: JSON.stringify({ headlineId, polarity }),
    },
  );
}

export function removeClassifierExample(
  classifierId: number,
  headlineId: number,
) {
  return request<{ stats: Record<string, unknown> }>(
    `/classifiers/${classifierId}/examples/${headlineId}`,
    { method: 'DELETE' },
  );
}

export function deleteClassifier(classifierId: number) {
  return request<void>(`/classifiers/${classifierId}`, { method: 'DELETE' });
}

export interface AutoClassifyProgress {
  phase: string;
  message: string;
  classifierId?: number;
  stats?: Record<string, unknown>;
}

export async function autoClassifyStream(
  name: string,
  concept: string,
  onProgress: (event: AutoClassifyProgress) => void,
): Promise<{ classifierId: number }> {
  const res = await fetch(`${base()}/classifiers/auto`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ name, concept }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed: ${res.status}`);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let classifierId = 0;
  let lastError: string | null = null;

  const processLines = (text: string) => {
    buffer += text;
    const lines = buffer.split('\n');
    buffer = lines.pop()!;

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      let event: AutoClassifyProgress;
      try {
        event = JSON.parse(line.slice(6));
      } catch {
        continue;
      }
      onProgress(event);
      if (event.phase === 'error') {
        lastError = event.message;
      }
      if (event.phase === 'done' && event.classifierId) {
        classifierId = event.classifierId;
      }
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      processLines(decoder.decode(value, { stream: true }));
    }
  } catch {
    // Stream closed — check if we already got a result or error
  }
  // Process any remaining buffer
  processLines('\n');

  if (lastError) {
    throw new Error(lastError);
  }

  if (!classifierId) {
    throw new Error('Auto-classify completed without returning a classifier');
  }

  return { classifierId };
}

export function searchCentroid(headlineIds: number[], searchType: string) {
  return request<{
    results: {
      headline_id: number;
      title: string;
      url: string;
      source: string;
      score: number;
      matchType: string;
    }[];
  }>('/centroid-search', {
    method: 'POST',
    body: JSON.stringify({ headlineIds, searchType }),
  });
}
